import { ToggleGroup } from 'radix-ui';
import type { Mode } from '@timato/shared';
import { cn } from '@/lib/cn';
import { useIsMobile } from '@/lib/useMediaQuery';

const MODES: { value: Mode; label: string; short: string }[] = [
  { value: 'focus', label: 'Focus', short: 'Focus' },
  { value: 'short', label: 'Short break', short: 'Short' },
  { value: 'long', label: 'Long break', short: 'Long' },
  { value: 'track', label: 'Track', short: 'Track' },
];

/**
 * Focus · Short break · Long break | Track (8a). A white card with a hairline border, the active mode an ink pill;
 * Track sits behind a divider because it is not part of the pomodoro cycle. `live` is the mode of a run going on
 * under a previewed one (TW-33): its tab keeps a dot, as the header's pill does, so the way back is in sight.
 */
export function ModeSwitch({ value, onChange, live }: { value: Mode; onChange: (m: Mode) => void; live?: Mode | null }) {
  const mobile = useIsMobile();
  return (
    <ToggleGroup.Root type="single" value={value} onValueChange={(v) => v && onChange(v as Mode)} aria-label="Timer mode"
      className="inline-flex items-center gap-1 p-1 rounded-btn border border-border bg-card">
      {MODES.map((m) => (
        <div key={m.value} className="contents">
          {m.value === 'track' && <span aria-hidden="true" className="w-px h-5 mx-[3px] bg-chip-border" />}
          <ToggleGroup.Item value={m.value}
            className={cn('rounded-[7px] border-0 cursor-pointer font-bold text-[12.5px] transition-colors text-ink-2 hover:text-ink data-[state=on]:bg-seg-on-bg data-[state=on]:text-seg-on-fg',
              mobile ? 'px-[11px] py-[6px]' : 'px-[15px] py-[7px]')}>
            {mobile ? m.short : m.label}
            {live === m.value && live !== value && <span aria-hidden="true" className="ml-[5px] text-accent text-[8px] align-middle">●</span>}
          </ToggleGroup.Item>
        </div>
      ))}
    </ToggleGroup.Root>
  );
}
